import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Plus, Search, Upload, Phone, Mail, BanIcon, Download } from "lucide-react";
import ContactsBulkPanel from "@/components/ContactsBulkPanel";

const blank = { name: "", phone: "", email: "", tags: "", dnd: false, opted_out: false };

export default function Contacts() {
  const [contacts, setContacts] = useState([]);
  const [lists, setLists] = useState([]);
  const [q, setQ] = useState("");
  const [listFilter, setListFilter] = useState("");
  const [selected, setSelected] = useState(new Set());
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(blank);
  const fileRef = useRef(null);

  const load = async () => {
    const { data } = await api.get("/contacts", { params: { q: q || undefined, list_id: listFilter || undefined } });
    setContacts(data);
  };
  useEffect(() => { load(); }, [q, listFilter]);
  useEffect(() => { api.get("/lists").then(r => setLists(r.data)); }, []);

  const allSelected = contacts.length > 0 && selected.size === contacts.length;
  const toggleAll = () => setSelected(allSelected ? new Set() : new Set(contacts.map(c => c.id)));
  const toggleOne = (id) => setSelected(s => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });

  const openCreate = () => { setEditing(null); setForm(blank); setOpen(true); };
  const openEdit = (c) => {
    setEditing(c);
    setForm({ name: c.name, phone: c.phone, email: c.email || "", tags: (c.tags || []).join(", "), dnd: !!c.dnd, opted_out: !!c.opted_out });
    setOpen(true);
  };

  const saveContact = async (e) => {
    e.preventDefault();
    const payload = {
      name: form.name, phone: form.phone, email: form.email || null,
      tags: form.tags.split(",").map(s => s.trim()).filter(Boolean),
      dnd: form.dnd, opted_out: form.opted_out,
    };
    try {
      if (editing) {
        await api.patch(`/contacts/${editing.id}`, payload);
        toast.success("Contact updated");
      } else {
        await api.post("/contacts", payload);
        toast.success("Contact added");
      }
      setOpen(false); setForm(blank); setEditing(null);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const importCSV = async (file) => {
    const fd = new FormData(); fd.append("file", file);
    try {
      const { data } = await api.post("/contacts/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const parts = [`Imported ${data.inserted}`];
      if (data.duplicates) parts.push(`${data.duplicates} duplicates`);
      if (data.error_count) parts.push(`${data.error_count} errors`);
      parts.push(`(${data.format?.toUpperCase() || "CSV"})`);
      toast.success(parts.join(" · "));
      if (data.error_count && data.errors?.length) {
        // Show a compact error summary in console for power users
        console.warn("Import errors:", data.errors);
      }
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Import failed");
    }
  };

  const exportCSV = async () => {
    const res = await api.get("/export/contacts.csv", { responseType: "blob" });
    const url = window.URL.createObjectURL(new Blob([res.data]));
    const a = document.createElement("a");
    a.href = url; a.download = "contacts.csv";
    document.body.appendChild(a); a.click(); a.remove();
    window.URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-4" data-testid="contacts-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground">Audience</div>
          <h1 className="text-3xl font-black tracking-tighter">Contacts</h1>
        </div>
        <div className="flex items-center gap-2">
          <input ref={fileRef} type="file" accept=".csv,.xlsx,.xls" className="hidden" onChange={e => e.target.files?.[0] && importCSV(e.target.files[0])} />
          <Button variant="outline" className="rounded-sm gap-2" onClick={exportCSV} data-testid="export-contacts-button">
            <Download className="h-4 w-4" /> Export
          </Button>
          <Button variant="outline" className="rounded-sm gap-2" onClick={() => fileRef.current?.click()} data-testid="import-contacts-button">
            <Upload className="h-4 w-4" /> Import CSV / Excel
          </Button>
          <Button className="rounded-sm gap-2" onClick={openCreate} data-testid="add-contact-button"><Plus className="h-4 w-4" /> New Contact</Button>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogContent className="rounded-sm">
              <DialogHeader><DialogTitle>{editing ? "Edit Contact" : "Add Contact"}</DialogTitle></DialogHeader>
              <form onSubmit={saveContact} className="space-y-3">
                <div><Label>Name</Label><Input required value={form.name} onChange={e => setForm({...form, name: e.target.value})} className="rounded-sm" data-testid="contact-name-input" /></div>
                <div><Label>Phone</Label><Input required value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} className="rounded-sm" data-testid="contact-phone-input" placeholder="+91..." /></div>
                <div><Label>Email</Label><Input type="email" value={form.email} onChange={e => setForm({...form, email: e.target.value})} className="rounded-sm" data-testid="contact-email-input" /></div>
                <div><Label>Tags (comma)</Label><Input value={form.tags} onChange={e => setForm({...form, tags: e.target.value})} className="rounded-sm" placeholder="vip, customer" data-testid="contact-tags-input" /></div>
                <div className="flex items-center gap-6 pt-1">
                  <label className="text-xs flex items-center gap-2"><Checkbox checked={form.dnd} onCheckedChange={v => setForm({...form, dnd: !!v})} data-testid="contact-dnd-checkbox" /> DND</label>
                  <label className="text-xs flex items-center gap-2"><Checkbox checked={form.opted_out} onCheckedChange={v => setForm({...form, opted_out: !!v})} data-testid="contact-optout-checkbox" /> Opted out</label>
                </div>
                <DialogFooter><Button type="submit" className="rounded-sm" data-testid="save-contact-button">{editing ? "Save" : "Add"}</Button></DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <Card className="rounded-sm shadow-none">
        <CardContent className="p-3 flex flex-wrap items-center gap-2 border-b border-border">
          <div className="relative">
            <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input placeholder="Search name, phone or email" value={q} onChange={e => setQ(e.target.value)}
              className="pl-8 w-72 rounded-sm" data-testid="contacts-search-input" />
          </div>
          <select value={listFilter} onChange={e => setListFilter(e.target.value)}
            className="h-9 px-3 rounded-sm border border-border bg-background text-sm" data-testid="list-filter-select">
            <option value="">All lists</option>
            {lists.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
          </select>
          {selected.size > 0 && (
            <ContactsBulkPanel
              selectedContacts={contacts.filter(c => selected.has(c.id))}
              lists={lists}
              onCleared={() => setSelected(new Set())}
              onChanged={load}
            />
          )}
        </CardContent>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-muted/40 sticky top-0">
              <tr className="text-left text-[10px] uppercase tracking-wider text-muted-foreground">
                <th className="px-3 py-2 w-10"><Checkbox checked={allSelected} onCheckedChange={toggleAll} data-testid="select-all-contacts" /></th>
                <th className="px-3 py-2">Name</th>
                <th className="px-3 py-2">Phone</th>
                <th className="px-3 py-2">Email</th>
                <th className="px-3 py-2">Tags</th>
                <th className="px-3 py-2">Flags</th>
              </tr>
            </thead>
            <tbody>
              {contacts.map(c => (
                <tr key={c.id} className="border-t border-border hover:bg-accent/40" data-testid={`contact-row-${c.id}`}>
                  <td className="px-3 py-2"><Checkbox checked={selected.has(c.id)} onCheckedChange={() => toggleOne(c.id)} /></td>
                  <td className="px-3 py-2"><Link to={`/contacts/${c.id}`} className="font-medium hover:underline" data-testid={`contact-link-${c.id}`}>{c.name}</Link></td>
                  <td className="px-3 py-2 font-mono text-xs flex items-center gap-1"><Phone className="h-3 w-3 text-muted-foreground" />{c.phone}</td>
                  <td className="px-3 py-2 text-xs"><span className="flex items-center gap-1"><Mail className="h-3 w-3 text-muted-foreground" />{c.email || "—"}</span></td>
                  <td className="px-3 py-2 space-x-1">
                    {(c.tags || []).map(t => <Badge key={t} variant="secondary" className="rounded-sm text-[10px]">{t}</Badge>)}
                  </td>
                  <td className="px-3 py-2 space-x-1">
                    {c.dnd && <Badge variant="outline" className="rounded-sm text-[10px] border-amber-300 text-amber-700">DND</Badge>}
                    {c.opted_out && <Badge variant="outline" className="rounded-sm text-[10px] border-red-300 text-red-700 gap-1"><BanIcon className="h-3 w-3" />Opted Out</Badge>}
                  </td>
                </tr>
              ))}
              {contacts.length === 0 && (
                <tr><td colSpan={6} className="text-center text-muted-foreground py-10">No contacts.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
